import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { waveform } from "ldrs";
import { AllocatedVehicleStore } from "./AllocatedVehicleStore";
import { CorporationStore } from "../corporation_master/CorporationStore";
import { AllOwnerTypeDetails } from "../../Apis/ownerTypeApi";
import { AllShiftMaster } from "../../Apis/shiftMasterApi";
import isGHMC from "../../Common/helper/helpar";

// Helper to resolve circle id from zone hierarchy objects
const getCircleId = (circle) =>
  circle?.circleId ?? circle?.CircleId ?? circle?.id ?? circle?.Id ?? null;

const getApiItems = (apiData) => {
  if (Array.isArray(apiData)) return apiData;
  if (Array.isArray(apiData?.data)) return apiData.data;
  if (Array.isArray(apiData?.result)) return apiData.result;
  return [];
};

const getApiCircleId = (item) =>
  item?.circleId ?? item?.CircleId ?? null;

const getApiVehicleCount = (item) => {
  const count = item?.vehiclesCount ?? item?.VehiclesCount;
  return count != null && count !== "" ? Number(count) : 0;
};

const buildVehicleCounts = (zoneCircles, apiData) => {
  const counts = {};
  const items = getApiItems(apiData);

  zoneCircles.forEach((zone) => {
    (zone.circles || []).forEach((circle) => {
      const circleId = getCircleId(circle);
      if (circleId == null) return;

      const apiItem = items.find(
        (item) => String(getApiCircleId(item)) === String(circleId)
      );

      counts[String(circleId)] = apiItem ? getApiVehicleCount(apiItem) : 0;
    });
  });

  return counts;
};

const AutoFetchOnFiltersReady = ({
  zoneCircles,
  isZoneCirclesLoading,
  onFetch,
}) => {
  const { values, setFieldValue } = useFormikContext();

  const zoneCircleKey = useMemo(
    () =>
      (zoneCircles || [])
        .flatMap((zone) => (zone.circles || []).map((circle) => getCircleId(circle)))
        .filter((id) => id != null)
        .join(","),
    [zoneCircles]
  );

  useEffect(() => {
    const { corporationId, ownerTypeId, shiftId } = values;
    if (!corporationId || !ownerTypeId || !shiftId || isZoneCirclesLoading) {
      return;
    }
    if (!zoneCircleKey) return;

    onFetch(corporationId, ownerTypeId, shiftId, setFieldValue);
  }, [
    values.corporationId,
    values.ownerTypeId,
    values.shiftId,
    zoneCircleKey,
    isZoneCirclesLoading,
    onFetch,
    setFieldValue,
  ]);

  return null;
};

const AllocatedForm = ({ handleClose }) => {
  waveform.register();

  const isProd = isGHMC();
  const themeColor = isProd ? "#007bff" : "#EC1117";
  const themeBg = isProd ? "#f0f6ff" : "#fff5f5";
  const themeBorder = isProd ? "#b3d1f7" : "#f5c0c2";

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlCorporationId = queryParams.get("corporationId");
  const urlOwnerTypeId = queryParams.get("ownerTypeId");
  const urlShiftId = queryParams.get("shiftId");

  const {
    ZoneCirclesDetails,
    isFetchZoneCirclesLoading,
    fetchZoneCirclesByCorporation,
    fetchAllocatedVehiclesByParams,
    isAllocatedVehicleLoading,
    saveAllocatedVehicleDetails,
  } = AllocatedVehicleStore();

  const { UserCorporations, fetchUserCorporations } = CorporationStore();

  const [ownerTypes, setOwnerTypes] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [autoPopulatedFromUrl, setAutoPopulatedFromUrl] = useState(false);
  const fetchRequestRef = useRef(0);

  const autoFetchAllocatedData = useCallback(
    async (corporationId, ownerTypeId, shiftId, setFieldValue) => {
      if (!corporationId || !ownerTypeId || !shiftId) {
        return;
      }

      const zoneCircles = AllocatedVehicleStore.getState().ZoneCirclesDetails;
      if (!zoneCircles?.length) {
        return;
      }

      const requestId = ++fetchRequestRef.current;

      try {
        const allocatedData = await fetchAllocatedVehiclesByParams(
          corporationId,
          ownerTypeId,
          shiftId
        );

        if (requestId !== fetchRequestRef.current) return;

        const freshZoneCircles = AllocatedVehicleStore.getState().ZoneCirclesDetails;
        const counts = buildVehicleCounts(freshZoneCircles, allocatedData);
        setFieldValue("vehicleCounts", counts, false);
      } catch (error) {
        if (requestId !== fetchRequestRef.current) return;
        console.error("Error fetching allocated data:", error);
        toast.error("Failed to load allocated vehicle details");
      }
    },
    [fetchAllocatedVehiclesByParams]
  );

  useEffect(() => {
    fetchUserCorporations();
    AllOwnerTypeDetails().then(setOwnerTypes).catch(() => { });
    AllShiftMaster().then(setShifts).catch(() => { });
  }, [fetchUserCorporations]);

  const validationSchema = Yup.object({
    corporationId: Yup.string().required("Corporation is required"),
    ownerTypeId: Yup.string().required("Owner Type is required"),
    shiftId: Yup.string().required("Shift is required"),
  });

  const initialValues = useMemo(
    () => ({
      corporationId: urlCorporationId || "",
      ownerTypeId: urlOwnerTypeId || "",
      shiftId: urlShiftId || "",
      vehicleCounts: {},
    }),
    [urlCorporationId, urlOwnerTypeId, urlShiftId]
  );

  // Auto-populate form and fetch data when URL parameters are provided
  useEffect(() => {
    if (urlCorporationId && urlOwnerTypeId && urlShiftId && !autoPopulatedFromUrl) {
      const fetchAndPopulateData = async () => {
        try {
          await fetchZoneCirclesByCorporation(urlCorporationId);
          setAutoPopulatedFromUrl(true);
        } catch (error) {
          console.error("Error auto-populating data:", error);
          toast.error("Failed to load zone/circle details");
        }
      };

      fetchAndPopulateData();
    }
  }, [urlCorporationId, urlOwnerTypeId, urlShiftId, autoPopulatedFromUrl, fetchZoneCirclesByCorporation]);

  const handleSubmit = async (values, { resetForm }) => {
      const data = [];
      ZoneCirclesDetails.forEach((zoneItem) => {
        if (!zoneItem.circles || zoneItem.circles.length === 0) return;
        zoneItem.circles.forEach((circle) => {
          const circleId = getCircleId(circle);
          const count = parseInt(values.vehicleCounts[String(circleId)], 10);
          data.push({
            zoneId: zoneItem.zoneId,
            circleId,
            vehicleCount: count > 0 ? count : 0,
          });
        });
      });

      const payload = {
        corporationId: parseInt(values.corporationId, 10),
        ownerTypeId: parseInt(values.ownerTypeId, 10),
        shiftId: parseInt(values.shiftId, 10),
        data,
      };

      try {
        const response = await saveAllocatedVehicleDetails(payload);
        if (response.status === "success") {
          toast.success("Allocated vehicle details saved successfully!");
          resetForm();
          handleClose && handleClose();
        } else {
          toast.error("Failed to save details.");
        }
      } catch (error) {
        toast.error(error.message || "Failed to save details.");
      }
    };

    return (
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <AutoFetchOnFiltersReady
              zoneCircles={ZoneCirclesDetails}
              isZoneCirclesLoading={isFetchZoneCirclesLoading}
              onFetch={autoFetchAllocatedData}
            />
            <div className="row g-3">
              {/* Corporation */}
              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label">
                    Corporation <span style={{ color: themeColor }}> *</span>
                  </label>
                  <Field
                    as="select"
                    name="corporationId"
                    className="form-select"
                    onChange={(e) => {
                      const id = e.target.value;
                      setFieldValue("corporationId", id);
                      setFieldValue("ownerTypeId", "");
                      setFieldValue("shiftId", "");
                      setFieldValue("vehicleCounts", {});
                      if (id) fetchZoneCirclesByCorporation(id);
                    }}
                  >
                    <option value="">Select</option>
                    {UserCorporations.map((corp) => (
                      <option key={corp.corporationId} value={corp.corporationId}>
                        {corp.corporationName}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="corporationId"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              {/* Shift */}
              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label">
                    Shift <span style={{ color: themeColor }}> *</span>
                  </label>
                  <Field
                    as="select"
                    name="shiftId"
                    className="form-select"
                    onChange={(e) => {
                      const shiftId = e.target.value;
                      setFieldValue("shiftId", shiftId);
                      setFieldValue("vehicleCounts", {});
                    }}
                  >
                    <option value="">Select</option>
                    {shifts.map((shift) => (
                      <option key={shift.shiftId} value={shift.shiftId}>
                        {shift.shiftName}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="shiftId"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              {/* Owner Type */}
              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label">
                    Owner Type <span style={{ color: themeColor }}> *</span>
                  </label>
                  <Field
                    as="select"
                    name="ownerTypeId"
                    className="form-select"
                    onChange={(e) => {
                      const ownerTypeId = e.target.value;
                      setFieldValue("ownerTypeId", ownerTypeId);
                      setFieldValue("vehicleCounts", {});
                    }}
                  >
                    <option value="">Select</option>
                    {ownerTypes.map((ot) => (
                      <option key={ot.ownerTypeId} value={ot.ownerTypeId}>
                        {ot.ownerTypeName}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="ownerTypeId"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              {/* Shift */}
              {/*  <div className="col-md-4">
              <div className="form-group">
                <label className="form-label">
                  Shift <span style={{ color: themeColor }}> *</span>
                </label>
                <Field as="select" name="shiftId" className="form-select">
                  <option value="">Select</option>
                  {shifts.map((shift) => (
                    <option key={shift.shiftId} value={shift.shiftId}>
                      {shift.shiftName}
                    </option>
                  ))}
                </Field>
                <ErrorMessage
                  name="shiftId"
                  component="div"
                  className="text-danger"
                />
              </div>
            </div>*/}

              {/* Circles with vehicle count — shown after corporation is selected */}
              {values.corporationId && (
                <div className="col-12">
                  {isFetchZoneCirclesLoading ? (
                    <div className="d-flex justify-content-center py-4">
                      <l-waveform size="20" stroke="3.5" speed="1" color={isProd ? "blue" : "red"} />
                    </div>
                  ) : (
                    ZoneCirclesDetails.filter((z) => z.circles && z.circles.length > 0).length > 0 && (
                      <div
                        style={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "10px",
                          overflow: "hidden",
                        }}
                      >
                        {/* Section header */}
                        <div
                          style={{
                            background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)",
                            padding: "10px 16px",
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          <span style={{ fontWeight: 600, fontSize: "13px", color: "#555", letterSpacing: "0.5px" }}>
                            VEHICLE COUNT PER CIRCLE
                          </span>
                        </div>

                        <div style={{ padding: "16px", maxHeight: "280px", overflowY: "auto" }}>
                          {ZoneCirclesDetails.filter((z) => z.circles && z.circles.length > 0).map(
                            (zoneItem, zoneIndex, arr) => (
                              <div
                                key={zoneItem.zoneId}
                                style={{
                                  marginBottom: zoneIndex < arr.length - 1 ? "20px" : 0,
                                }}
                              >
                                {/* Zone label */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginBottom: "10px",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "4px",
                                      height: "18px",
                                      borderRadius: "2px",
                                      background: themeColor,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span style={{ fontWeight: 600, fontSize: "14px", color: "#333" }}>
                                    {zoneItem.zoneName}
                                  </span>
                                </div>

                                {/* Circles grid */}
                                <div className="row g-2" style={{ paddingLeft: "12px" }}>
                                  {zoneItem.circles.map((circle) => {
                                    const circleId = getCircleId(circle);
                                    const circleKey = String(circleId);
                                    const countValue = values.vehicleCounts[circleKey];
                                    const hasValue = Number(countValue) > 0;
                                    return (
                                      <div key={circleKey} className="col-md-4 col-sm-6">
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            background: hasValue ? themeBg : "#fafafa",
                                            border: `1px solid ${hasValue ? themeBorder : "#e8e8e8"}`,
                                            borderRadius: "8px",
                                            padding: "8px 10px",
                                            gap: "10px",
                                            transition: "all 0.2s",
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: "8px",
                                              height: "8px",
                                              borderRadius: "50%",
                                              background: hasValue ? themeColor : "#ccc",
                                              flexShrink: 0,
                                              transition: "background 0.2s",
                                            }}
                                          />
                                          <span
                                            style={{
                                              fontSize: "13px",
                                              color: "#444",
                                              flex: 1,
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                            title={circle.circleName}
                                          >
                                            {circle.circleName}
                                          </span>
                                          <Field
                                            type="number"
                                            min="0"
                                            name={`vehicleCounts[${circleKey}]`}
                                            autoComplete="off"
                                            inputMode="numeric"
                                            style={{
                                              width: "70px",
                                              border: "1px solid #ddd",
                                              borderRadius: "6px",
                                              padding: "4px 8px",
                                              fontSize: "13px",
                                              textAlign: "center",
                                              outline: "none",
                                              flexShrink: 0,
                                            }}
                                            placeholder="0"
                                          />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Zone divider */}
                                {zoneIndex < arr.length - 1 && (
                                  <hr style={{ margin: "16px 0 0", borderColor: "#eeee" }} />
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}

              {/* Submit */}
              <div className="modal-footer d-flex justify-content-center">
                <button
                  type="submit"
                  className="deployment-button"
                  disabled={isAllocatedVehicleLoading}
                >
                  {isAllocatedVehicleLoading ? (
                    <l-waveform size="20" stroke="3.5" speed="1" color={isProd ? "blue" : "red"} />
                  ) : (
                    <>
                      <i className="fa-solid fa-check done-container"></i>
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    );
};
export default AllocatedForm;
